/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { Controller } from "react-hook-form";
import { InputFieldProps } from "../../types/InputProps";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import InputWrapper from "./InputWrapper";
import { DatePicker } from "../Datepicker";

const FormDatePicker = (props: InputFieldProps) => {
  const {
    name,
    control,
    label,
    info,
    hint,
    meta = {},
    validations = [],
  } = props;

  // Extract additional props from meta if provided
  const {
    disablePast,
    disableFuture,
    onChange: customOnChange,
    ...metaWithoutKey
  } = meta;

  const required = validations.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const hasError = Boolean(error);

          // Parse the value to a Dayjs object if it's a valid string, otherwise set to null
          let parsedValue: Dayjs | null = null;

          if (dayjs.isDayjs(value)) {
            parsedValue = value;
          } else if (typeof value === "string") {
            const parsed = dayjs(value, "MM/DD/YYYY", true);
            parsedValue = parsed.isValid() ? parsed : null;
          }

          return (
            <InputWrapper info={info} hint={hint} error={error}>
              <DatePicker
                {...metaWithoutKey}
                name={name}
                control={control}
                disablePast={disablePast}
                disableFuture={disableFuture}
                label={label}
                required={required}
                fullWidth
                errors={hasError ? { [name]: error } : undefined}
                value={parsedValue}
                onChange={(date: Dayjs | null) => {
                  const formattedDate =
                    date && date.isValid() ? date.format("MM/DD/YYYY") : null;
                  onChange(formattedDate);
                  if (customOnChange) customOnChange(formattedDate);
                }}
                slotProps={{
                  textField: {
                    required: required,
                    error: !!error,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-asterisk": {
                    color: "#F44336",
                  },
                }}
              />
            </InputWrapper>
          );
        }}
      />
    </LocalizationProvider>
  );
};

export default FormDatePicker;
